import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "./icons";

/**
 * Playing a delivered file straight from the file list.
 *
 * Checking a delivery usually starts with a listen - is this the right take,
 * does it start where it should, is the room tone the same as yesterday's - and
 * until now that meant opening a service or downloading the file. This is the
 * short way round: press play on the row.
 *
 * ONE element for the whole list, not one per row. Forty <audio> tags would
 * each hold a connection open on a bucket, and the important behaviour - that
 * starting a second file stops the first - comes free from there being only one
 * thing playing. It is also why the skip buttons are dead until a row is the
 * one playing: seeking a file that is not loaded has nothing to seek.
 */

export const SKIP_S = 15;

export interface RowPlayer {
  file: string | null;
  playing: boolean;
  at: number;
  toggle: (file: string) => void;
  skip: (file: string, delta: number) => void;
  /** Spread onto a single <audio> the list renders once. */
  audioProps: {
    ref: React.RefObject<HTMLAudioElement>;
    preload: "none";
    onTimeUpdate: () => void;
    onEnded: () => void;
    onPause: () => void;
    onPlay: () => void;
  };
}

export function useRowPlayer(projectId: string): RowPlayer {
  const ref = useRef<HTMLAudioElement>(null);
  const [file, setFile] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);

  // Leaving the page must not leave audio running behind it.
  useEffect(() => () => { ref.current?.pause(); }, []);

  const play = useCallback((a: HTMLAudioElement) => {
    const p = a.play();
    if (p && typeof p.catch === "function") p.catch(() => setPlaying(false));
  }, []);

  const toggle = useCallback((name: string) => {
    const a = ref.current;
    if (!a) return;
    if (file !== name) {
      // A different file: point the one element at it and start. Whatever was
      // playing stops by definition.
      a.pause();
      a.src = `/api/projects/${projectId}/audio/${encodeURIComponent(name)}/stream`;
      a.currentTime = 0;
      setFile(name);
      setAt(0);
      play(a);
      return;
    }
    if (a.paused) play(a); else a.pause();
  }, [file, play, projectId]);

  const skip = useCallback((name: string, delta: number) => {
    const a = ref.current;
    if (!a || file !== name) return;
    const dur = Number.isFinite(a.duration) ? a.duration : Infinity;
    a.currentTime = Math.max(0, Math.min(dur - 0.25, a.currentTime + delta));
    setAt(a.currentTime);
  }, [file]);

  return {
    file, playing, at, toggle, skip,
    audioProps: {
      ref,
      preload: "none",
      onTimeUpdate: () => setAt(ref.current?.currentTime ?? 0),
      onEnded: () => { setPlaying(false); setAt(0); },
      onPause: () => setPlaying(false),
      onPlay: () => setPlaying(true),
    },
  };
}

/** The three controls on one row. */
export function RowTransport({ file, player, disabled }: {
  file: string;
  player: RowPlayer;
  disabled?: boolean;
}) {
  const isThis = player.file === file;
  const isPlaying = isThis && player.playing;
  return (
    <span className="rowplay" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="rowplay-btn"
        disabled={disabled || !isThis}
        title={isThis ? `Back ${SKIP_S} seconds` : `Press play first, then skip back ${SKIP_S} seconds`}
        aria-label={`Back ${SKIP_S} seconds`}
        onClick={() => player.skip(file, -SKIP_S)}>
        <Icon name="rotate-ccw" size={13} />
      </button>
      <button type="button" className="rowplay-btn main"
        disabled={disabled}
        title={isPlaying ? `Pause ${file}` : `Play ${file}`}
        aria-label={isPlaying ? "Pause" : "Play"}
        onClick={() => player.toggle(file)}>
        <Icon name={isPlaying ? "pause" : "play"} size={13} />
      </button>
      <button type="button" className="rowplay-btn"
        disabled={disabled || !isThis}
        title={isThis ? `Forward ${SKIP_S} seconds` : `Press play first, then skip forward ${SKIP_S} seconds`}
        aria-label={`Forward ${SKIP_S} seconds`}
        onClick={() => player.skip(file, SKIP_S)}>
        <Icon name="rotate-cw" size={13} />
      </button>
    </span>
  );
}
